import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, BedDouble, Bath, Maximize2, ShieldCheck, Star, Heart } from "lucide-react";
import { formatINR, formatArea, formatPricePerSqft, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=900&q=80";

/** Optimistic like/save toggle shared by the card heart button. */
function useLikeToggle(p, initialLiked, onLikeChange) {
  const { user, promptLogin, refresh } = useAuth();
  const isLikedFromUser = (user?.liked_properties || []).includes(p.id);
  const [liked, setLiked] = useState(initialLiked ?? isLikedFromUser);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (typeof initialLiked === "boolean") setLiked(initialLiked);
    else setLiked((user?.liked_properties || []).includes(p.id));
  }, [initialLiked, user, p.id]);

  const toggleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.message("Sign in to save properties");
      promptLogin();
      return;
    }
    if (busy) return;
    setBusy(true);
    const next = !liked;
    setLiked(next);
    try {
      if (next) await api.post(`/my/likes/${p.id}`);
      else await api.delete(`/my/likes/${p.id}`);
      refresh?.();
      onLikeChange?.(p.id, next);
    } catch {
      setLiked(!next);
      toast.error("Failed to update saved list");
    } finally {
      setBusy(false);
    }
  };

  return { liked, toggleLike };
}

function CardBadges({ p }) {
  return (
    <div className="absolute top-3 left-3 flex flex-col gap-2">
      {p.is_sold && (
        <span className="trust-badge bg-red-600 text-white border-red-600">
          Sold Out
        </span>
      )}
      {p.verified && !p.is_sold && (
        <span className="trust-badge bg-gold text-white border-gold">
          <ShieldCheck className="w-3 h-3" /> Verified
        </span>
      )}
      {p.featured && !p.is_sold && (
        <span className="trust-badge bg-ink text-white border-ink">
          <Star className="w-3 h-3" /> Premium
        </span>
      )}
    </div>
  );
}

function LikeButton({ p, liked, onToggle }) {
  return (
    <button
      onClick={onToggle}
      data-testid={`like-btn-${p.id}`}
      aria-label={liked ? "Unsave property" : "Save property"}
      aria-pressed={liked}
      className={`absolute top-3 right-3 w-9 h-9 flex items-center justify-center border transition-all ${
        liked
          ? "bg-red-500 border-red-500 text-white"
          : "bg-white/95 border-white text-ink hover:text-red-500"
      }`}
    >
      <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} />
    </button>
  );
}

function CardBody({ p }) {
  const rate = formatPricePerSqft(p.price, p.area_value, p.area_unit, p.listing_type);
  return (
    <div className="p-5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <MapPin className="w-3.5 h-3.5 text-gold" />
        <span>{p.locality}, {p.city}</span>
      </div>
      <h3 className="font-display text-lg lg:text-xl text-ink mt-2 leading-snug line-clamp-2">{p.title}</h3>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-2xl font-bold text-ink">{formatINR(p.price)}</span>
        {p.listing_type === "rent" && <span className="text-xs text-muted-foreground">/month</span>}
      </div>
      {rate && <div className="text-[11px] text-muted-foreground mt-1" data-testid={`price-per-sqft-${p.id}`}>{rate}</div>}
      <div className="mt-4 flex items-center gap-4 text-xs text-foreground/70 pt-4 border-t border-border">
        {p.bedrooms > 0 && <div className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{p.bedrooms} Bed</div>}
        {p.bathrooms > 0 && <div className="flex items-center gap-1"><Bath className="w-3.5 h-3.5" />{p.bathrooms} Bath</div>}
        <div className="flex items-center gap-1"><Maximize2 className="w-3.5 h-3.5" />{formatArea(p.area_value, p.area_unit)}</div>
      </div>
    </div>
  );
}

export default function PropertyCard({ p, index = 0, liked: initialLiked, onLikeChange }) {
  const { liked, toggleLike } = useLikeToggle(p, initialLiked, onLikeChange);
  const img = p.images?.[0] || FALLBACK_IMG;
  return (
    <Link
      to={`/property/${p.slug || p.id}`}
      data-testid={`property-card-${p.id}`}
      className="property-card group block animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={img}
          alt={p.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <CardBadges p={p} />
        <LikeButton p={p} liked={liked} onToggle={toggleLike} />
        <div className="absolute bottom-3 right-3 bg-white/95 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-ink">
          {p.property_type}
        </div>
      </div>
      <CardBody p={p} />
    </Link>
  );
}
